import { createRouter, createWebHistory } from 'vue-router';
import clientsRoutes from '@/router/client.routes';
import modifyRoutes from '@/router/modify/routes.js';
import adminRoutes from '@/router/admin.routes';
import Tr from '@/i18n/translation';
import { IProductBundleState } from 'interface/db/userProductBundle.js';
import { RouteNames as ModifyRouteNames } from './modify/names.js';
import { useModifyLinkStore } from '@/stores/modifyLink/modifyLink.store.js';
import { ProductType } from 'interface/data/productTypes.js';

const setLanguage = async () => {
  const language = localStorage.getItem('language');
  if (language) {
    await Tr.switchLanguage(language);
  } else {
    await Tr.switchLanguage('pl');
  }
};

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/link/:id',
      beforeEnter: async (to, _from, next) => {
        const linkId = to.params['id'];
        const { trySetupData } = useModifyLinkStore();
        await trySetupData((linkId as string) ?? '');
        const { getLinkData } = useModifyLinkStore();
        localStorage.setItem('language', getLinkData.languages[0] ?? 'pl');
        setLanguage();
        const params = to.params;
        if (getLinkData.isProductLink) {
          switch (getLinkData.productType) {
            case ProductType.EVENT:
              return next(
                Tr.i18nRoute({ name: ModifyRouteNames.Event, params }),
              );
          }
        }
        switch (getLinkData.bundleState) {
          case IProductBundleState.NEW:
            return next(
              Tr.i18nRoute({ name: ModifyRouteNames.TokenDate, params }),
            );
          case IProductBundleState.UNKNOW:
            return next(Tr.i18nRoute({ name: 'Home' }));
          default:
            return next(
              Tr.i18nRoute({ name: ModifyRouteNames.FormFulfilled, params }),
            );
        }
      },
      component: () => import('@/views/HomeView.vue'),
      name: 'Link',
    },
    {
      path: '/:locale?',
      beforeEnter: Tr.routeMiddleware,
      children: [
        {
          path: '',
          name: 'Home',
          component: () => import('@/views/HomeView.vue'),
        },
        { ...clientsRoutes },
        { ...modifyRoutes },
        { ...adminRoutes },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: () => {
        setLanguage();
        return Tr.i18nRoute({ name: 'Home' });
      },
    },
  ],
  scrollBehavior(to, _from, savedPosition) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: to.query['smooth'] ? 'smooth' : 'auto',
      };
    } else if (to.meta['stayInPosition']) {
      return;
    } else if (savedPosition) {
      return savedPosition;
    } else {
      return { left: 0, top: 0 };
    }
  },
});

router.beforeEach(async (to, _from, next) => {
  let locale = to.params['locale'] ?? to.matched[0]?.path.substr(1);
  if (locale === 'uk') {
    // TODO Workaround for routing uk -> ua
    locale = 'ua';
  }

  if (Tr.supportedLocales.includes((locale as string) ?? '')) {
    localStorage.setItem('language', locale as string);
    await Tr.switchLanguage(locale);
  } else {
    localStorage.setItem('language', 'pl');
    await Tr.switchLanguage('pl');
  }

  next();
});

export default router;
